import { fmt, pct } from "@/utils/dataEntryDerived";

const deriveTimelineDays = (gapCount, totalSchools) => {
  if (!gapCount || !totalSchools) return 30;
  const ratio = gapCount / totalSchools;
  if (ratio > 0.25) return 15;
  if (ratio > 0.1) return 30;
  if (ratio > 0.03) return 45;
  return 60;
};

export const buildClassroomsToiletsLocalGuidance = ({
  overview,
  riskSchools,
  equityMetrics,
  hygieneMetrics,
  blockData,
}) => {
  if (!overview) return null;

  const riskSummary = riskSchools?.risk_summary ?? {};
  const zeroBoys = overview.zero_boys_toilet_schools ?? 0;
  const zeroGirls = overview.zero_girls_toilet_schools ?? 0;
  const zeroToiletTotal = riskSummary.zero_toilet_schools ?? zeroBoys + zeroGirls;
  const waterGap = overview.water_gap ?? 0;
  const handwashPoints = overview.handwash_points ?? hygieneMetrics?.handwash_points ?? 0;
  const sufficiency = overview.handwash_sufficiency_ratio ?? hygieneMetrics?.handwash_sufficiency ?? 0;
  const cwsnCoverage = equityMetrics?.cwsn_coverage_pct ?? 0;
  const cwsnGap = Math.round((100 - cwsnCoverage) * 10) / 10;
  const majorRepairSchools = riskSummary.major_repair_schools ?? 0;
  const majorRepairClassrooms = overview.classrooms_major_repair ?? 0;
  const totalSchools = overview.total_schools ?? 1;
  const noWaterSchools = riskSummary.no_water_schools ?? 0;
  const noHandwashSchools = riskSummary.no_handwash_schools ?? 0;

  const criticalExceptions =
    (riskSummary.zero_toilet_schools ?? 0) +
    (riskSummary.no_water_schools ?? 0) +
    (riskSummary.no_handwash_schools ?? 0) +
    (riskSummary.major_repair_schools ?? 0);

  const actionFocusDays = deriveTimelineDays(criticalExceptions, totalSchools);
  const aiReadiness =
    overview.infrastructure_readiness_index ??
    ((overview.classroom_health_index ?? 0) +
      (overview.toilet_functional_pct ?? 0) +
      (overview.water_coverage_pct ?? 0)) /
      3;

  const diagnosticSignals = [
    {
      title: "Water Supply Is the Main Usability Constraint",
      description: `Toilet functionality is high, but ${fmt(waterGap)} toilets are not fully usable due to lack of water.`,
      pillLabel: "High",
      tone: "warn",
    },
    {
      title: "Major Repair Backlog Needs Safety Prioritisation",
      description: `${fmt(majorRepairSchools)} schools require major repairs, with ${fmt(majorRepairClassrooms)} classrooms needing urgent repair attention.`,
      pillLabel: "Critical",
      tone: "red",
    },
    {
      title: "Handwash Sufficiency Is Below Ideal",
      description: `${fmt(handwashPoints)} handwash points produce a sufficiency ratio of ${sufficiency.toFixed(2)}, below the ideal 1.0 benchmark.`,
      pillLabel: "Medium",
      tone: "warn",
    },
    {
      title: "Inclusion Readiness Is Good but Not Complete",
      description: `CWSN school coverage is ${pct(cwsnCoverage)}, leaving a ${pct(cwsnGap)} coverage gap that needs targeted closure.`,
      pillLabel: "Good",
      tone: "good",
    },
  ];

  const statusFromRatio = (ratio) => {
    if (ratio > 0.2) return { status: "Critical", statusTone: "danger" };
    if (ratio > 0.08) return { status: "High", statusTone: "danger" };
    if (ratio > 0.02) return { status: "Medium", statusTone: "warn" };
    return { status: "Monitor", statusTone: "blue" };
  };

  const actionQueue = [
    {
      priority: 1,
      area: "Zero-toilet schools",
      detail: "Schools with missing boys/girls toilets",
      signal: `${fmt(zeroToiletTotal)} schools`,
      owner: "Block Engineer + BEO",
      timeline: `0-${deriveTimelineDays(zeroToiletTotal, totalSchools)} days`,
      ...statusFromRatio(zeroToiletTotal / totalSchools),
    },
    {
      priority: 2,
      area: "Toilet water gap",
      detail: "Functional toilets lacking water supply",
      signal: `${fmt(waterGap)} toilets`,
      owner: "WASH Officer",
      timeline: `0-${deriveTimelineDays(waterGap, Math.max(overview.total_toilets ?? 1, 1))} days`,
      ...statusFromRatio(waterGap / Math.max(overview.total_toilets ?? 1, 1)),
    },
    {
      priority: 3,
      area: "Major classroom repairs",
      detail: "Safety and usability repair backlog",
      signal: `${fmt(majorRepairClassrooms)} classrooms`,
      owner: "Infrastructure Cell",
      timeline: `0-${deriveTimelineDays(majorRepairSchools, totalSchools)} days`,
      ...statusFromRatio(majorRepairSchools / totalSchools),
    },
    {
      priority: 4,
      area: "Handwash sufficiency",
      detail: "Points per toilet below ideal ratio",
      signal: `${sufficiency.toFixed(2)} ratio`,
      owner: "School Head + Cluster",
      timeline: `${deriveTimelineDays(Math.max(0, 1 - sufficiency) * 100, 100)} days`,
      ...statusFromRatio(sufficiency < 1 ? 1 - sufficiency : 0),
    },
    {
      priority: 5,
      area: "CWSN accessibility closure",
      detail: "Remaining accessibility gap",
      signal: `${pct(cwsnGap)} gap`,
      owner: "Inclusive Education Officer",
      timeline: `${deriveTimelineDays(cwsnGap, 100)} days`,
      ...statusFromRatio(cwsnGap / 100),
    },
  ];

  const closurePlan = [
    {
      num: "01",
      title: "Create Verified Exception List",
      p: `Freeze ${fmt(zeroToiletTotal)} zero-toilet, ${fmt(noWaterSchools)} no-water, ${fmt(noHandwashSchools)} no-handwash and ${fmt(majorRepairSchools)} major-repair schools for closure tracking.`,
    },
    {
      num: "02",
      title: "Assign Block Owners",
      p: `Allocate owners across ${fmt(overview.total_blocks ?? 0)} blocks for ${fmt(criticalExceptions)} combined risk exceptions with weekly status updates.`,
    },
    {
      num: "03",
      title: "Prioritise Safety-First Repairs",
      p: `Close ${fmt(majorRepairClassrooms)} major-repair classrooms in ${fmt(majorRepairSchools)} schools before usability fixes.`,
    },
    {
      num: "04",
      title: "Review with Evidence",
      p: `Target ${pct(overview.toilet_functional_pct)} toilet functionality and ${pct(overview.water_coverage_pct)} water coverage with verified school records.`,
    },
  ];

  const primarySignal =
    diagnosticSignals.find((s) => s.tone === "red")?.description ??
    diagnosticSignals.find((s) => s.tone === "warn")?.description ??
    diagnosticSignals[0]?.description ??
    "";

  return {
    insights: {
      hero_summary: `Infrastructure readiness is ${pct(aiReadiness)} across ${fmt(totalSchools)} schools with ${fmt(criticalExceptions)} critical WASH exceptions (${fmt(waterGap)} water gap, ${fmt(zeroToiletTotal)} zero-toilet schools).`,
      primary_signal: primarySignal,
      action_focus_days: actionFocusDays,
      briefing_cards: [
        {
          key: "baseline",
          description: `${fmt(overview.total_classrooms)} classrooms across ${fmt(totalSchools)} schools with ${pct(overview.repair_backlog_pct ?? 0)} repair backlog.`,
        },
        {
          key: "water",
          description: `${fmt(waterGap)} functional toilets lack reliable water supply.`,
        },
        {
          key: "risk",
          description: `${fmt(zeroToiletTotal)} schools lack adequate toilet access (${fmt(zeroBoys)} boys, ${fmt(zeroGirls)} girls).`,
        },
        {
          key: "action",
          description: `Run a ${actionFocusDays}-day WASH closure sprint across ${fmt(criticalExceptions)} tracked exceptions.`,
        },
      ],
      diagnostic_signals: diagnosticSignals,
      action_queue: actionQueue,
      closure_plan: closurePlan,
      closure_target: `Complete ${fmt(criticalExceptions)} tracked exceptions within ${actionFocusDays} days at current risk ratio (${pct((criticalExceptions / totalSchools) * 100, 2)} of schools).`,
    },
  };
};

export const buildClassroomsToiletsRiskMarkdown = ({ riskSchools, topBottomBlocks, blockData }) => {
  const summary = riskSchools?.risk_summary || {};
  const zeroToilet = summary.zero_toilet_schools ?? 0;
  const noWater = summary.no_water_schools ?? 0;
  const noHandwash = summary.no_handwash_schools ?? 0;
  const majorRepair = summary.major_repair_schools ?? 0;
  const bottomBlocks = (topBottomBlocks?.bottom_blocks || []).slice(0, 3).map((b) => b.block_name).join(", ");

  return [
    "## Insights",
    `- ${zeroToilet} schools have toilet gaps, ${noWater} lack water and ${noHandwash} lack handwash facilities.`,
    `- Critical exceptions should be triaged by boys+girls gap severity before WASH compliance review.`,
    "",
    "## Root Cause Signals",
    `- Toilet gap schools: ${zeroToilet} (${(summary.zero_toilet_pct ?? 0).toFixed?.(2) ?? summary.zero_toilet_pct ?? 0}%).`,
    `- No-water schools: ${noWater}; major repair backlog: ${majorRepair}.`,
    bottomBlocks ? `- Weakest blocks: ${bottomBlocks}.` : "",
    "",
    "## Recommendations",
    `- Prioritize schools with both boys and girls toilet gaps for 7-day closure.`,
    `- Schedule water and handwash remediation for high-risk blocks within 15 days.`,
    "",
    "## Priority Action Items",
    `- Assign BEO owners to top ${Math.min(20, (riskSchools?.zero_toilet_schools || []).length)} toilet-gap schools.`,
    `- Export and review the critical school action queue daily until exceptions fall below district threshold.`,
    `- Coordinate engineer visits for ${majorRepair} major-repair schools in ${(blockData || []).length} tracked blocks.`,
  ]
    .filter(Boolean)
    .join("\n");
};

export const buildClassroomsToiletsMetricsPayload = ({
  overview,
  riskSchools,
  equityMetrics,
  hygieneMetrics,
  blockData,
  scopeLabel,
}) => ({
  scope_label: scopeLabel,
  overview,
  risk_summary: riskSchools?.risk_summary,
  equity_metrics: equityMetrics,
  hygiene_metrics: hygieneMetrics,
  block_summary: (blockData ?? []).slice(0, 10).map((b) => ({
    block_name: b.block_name,
    wash_index: b.wash_index ?? b.wash_compliance_index,
    classroom_health: b.classroom_health_index,
    toilet_functional_pct: b.toilet_functional_pct,
  })),
});
